import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchFootprints } from '../services/footprintService';

// 足跡の表示期限（ms）
const NORMAL_TTL_MS  = 48 * 60 * 60 * 1000; // 48時間
const SUPER_TTL_MS   = 72 * 60 * 60 * 1000; // 72時間（すっごーい！足あと）

/**
 * 投稿から現在時刻までの残り時間(ms)を返す
 * 期限切れは負数
 */
export const getRemainingMs = (post) => {
  const ttl = post.isSuper ? SUPER_TTL_MS : NORMAL_TTL_MS;
  return new Date(post.createdAt).getTime() + ttl - Date.now();
};

/**
 * 残り時間に応じたピン色を返す
 * 🟢 緑: すっごーい！足あと
 * 🔵 青: 残り24h以上
 * 🟡 黄: 残り12h〜24h
 * 🔴 赤: 残り12h未満
 */
export const getFootprintColor = (post) => {
  if (post.isSuper) return '#22c55e'; // green-500#3b82f6
  const rem = getRemainingMs(post);
  if (rem > 24 * 60 * 60 * 1000) return '#3b82f6'; // blue-500
  if (rem > 12 * 60 * 60 * 1000) return '#eab308'; // yellow-500
  return '#ef4444'; // red-500
};

/**
 * 足跡一覧を取得・消滅フィルタリングするHook
 */
const useFootprints = () => {
  const [allPosts, setAllPosts] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // TTL内の有効な足あとを全件取得（ページを自動でたどる）
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let collected = [];
      let currentCursor = null;
      // hasMore が false になるまで全ページ取得
      while (true) {
        const result = await fetchFootprints({ limit: 500, cursor: currentCursor });
        collected = [...collected, ...result.posts];
        if (!result.hasMore) break;
        currentCursor = result.nextCursor;
      }
      setAllPosts(collected);
      setCursor(null);
      setHasMore(false);
    } catch {
      setError('足跡の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // 消滅済みを除外した表示対象のみ
  const posts = useMemo(
    () => allPosts.filter((p) => getRemainingMs(p) > 0),
    [allPosts]
  );

  const loadMore = () => {}; // 全件自動取得のため不要（互換性のため残す）

  // 全件再取得（投稿完了後などに使用）
  const reload = useCallback(() => { load(); }, [load]);

  // 特定投稿の reactionCount をローカルで更新（ボトムシートの「の」操作後に使用）
  const updateReactionCount = useCallback((postId, newCount) => {
    setAllPosts((prev) =>
      prev.map((p) => p.postId === postId ? { ...p, reactionCount: newCount } : p)
    );
  }, []);

  return { posts, loading, error, hasMore, loadMore, reload, updateReactionCount };
};

export default useFootprints;
